import { useMutation } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import {
  TemplateVersion,
  TemplateDeployment,
  BlueprintInput,
  BlueprintInputType,
  TemplateDeploymentType,
} from "types/generated";

import { buildDefaultValues, buildUpdateInputsPayload } from "./helpers";
import { TEMPLATE_CREATE_STACK, TEMPLATE_UPDATE_VERSION_DEPLOYMENT } from "./gql";
import { SecretFieldAction, SecretFieldsState, VersionTemplateFormFields } from "./types";

type UseDeploymentFormOptions = {
  version: TemplateVersion;
  deployment?: TemplateDeployment;
  templateId?: string;
  deploymentType?: TemplateDeploymentType;
  submitCallback?: (deploymentId?: string) => void;
};

const useDeploymentForm = ({
  version,
  deployment,
  templateId,
  deploymentType = TemplateDeploymentType.Regular,
  submitCallback,
}: UseDeploymentFormOptions) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [inputs, setInputs] = useState<BlueprintInput[]>(version.inputs);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const hasErrors = errors.length > 0;
  const hasPreview = inputs.length > 0;
  const isUpdateMode = !!deployment;

  const secretInputIds = useMemo(
    () => new Set(inputs.filter((i) => i.type === BlueprintInputType.Secret).map((i) => i.id)),
    [inputs]
  );

  // Track which secret inputs have existing values in the deployment
  const existingSecretIds = useMemo(() => {
    if (!isUpdateMode || !deployment) return new Set<string>();
    return new Set(deployment.inputs.filter((di) => secretInputIds.has(di.id)).map((di) => di.id));
  }, [isUpdateMode, deployment, secretInputIds]);

  const [createDeployment, { loading: deploymentCreateLoading }] =
    useMutation(TEMPLATE_CREATE_STACK);
  const [updateDeployment, { loading: deploymentUpdateLoading }] = useMutation(
    TEMPLATE_UPDATE_VERSION_DEPLOYMENT
  );

  const loading = deploymentCreateLoading || deploymentUpdateLoading;

  const defaultValues = useMemo(
    () =>
      buildDefaultValues({
        inputs,
        version,
        deployment,
        isUpdateMode,
        existingSecretIds,
      }),
    [inputs, version, deployment, isUpdateMode, existingSecretIds]
  );

  const builderForm = useForm<VersionTemplateFormFields>({
    defaultValues,
    mode: "onChange",
  });

  const {
    reset,
    formState: { isDirty: formIsDirty },
  } = builderForm;

  // Track the intended action for each secret field (missing = KEEP)
  const [secretFieldActions, setSecretFieldActions] = useState<Map<string, SecretFieldAction>>(
    new Map()
  );

  // Check if there are any secret action changes
  const hasSecretActions = secretFieldActions.size > 0;

  // Form is considered dirty if either the form fields changed OR secret actions were set
  const isDirty = formIsDirty || hasSecretActions;

  const setSecretAction = useCallback(
    (inputId: string, action: SecretFieldAction) => {
      setSecretFieldActions((prev) => {
        const next = new Map(prev);
        if (action === SecretFieldAction.KEEP) {
          next.delete(inputId);
        } else {
          next.set(inputId, action);
        }
        return next;
      });

      // Always clear the form value so re-entering CHANGE starts fresh
      builderForm.setValue(`inputs.${inputId}`, "");
    },
    [builderForm]
  );

  const secretFieldsState: SecretFieldsState = useMemo(
    () => ({
      fieldActions: secretFieldActions,
      setAction: setSecretAction,
      existingSecretIds,
    }),
    [secretFieldActions, setSecretAction, existingSecretIds]
  );

  const onSubmit: SubmitHandler<VersionTemplateFormFields> = (formData) => {
    if (isUpdateMode && deployment) {
      // Update mode: use BlueprintDeploymentUpdateInputPair with keepOldValue
      const updateInputsPayload = buildUpdateInputsPayload({
        formInputs: formData.inputs,
        secretInputIds,
        existingSecretIds,
        secretFieldActions,
      });
      const hasVersionChanged =
        formData.versionId && formData.versionId !== deployment.templateVersion.id;

      updateDeployment({
        variables: {
          templateID: templateId!,
          id: deployment.id,
          input: {
            name: formData.name,
            description: formData.description,
            inputs: updateInputsPayload,
            versionId: hasVersionChanged ? formData.versionId! : null,
          },
        },
      })
        .then(({ data }) => {
          if (data?.templateDeploymentUpdate) {
            if (submitCallback) {
              submitCallback(data.templateDeploymentUpdate.id);
            }

            reportSuccess({
              message: `Deployment was successfully updated`,
            });
          }
        })
        .catch(onError);
    } else {
      // Create mode: use BlueprintDeploymentCreateInputPair with sensitive flag
      const createInputsPayload = Object.entries(formData.inputs).map(([key, value]) => ({
        id: key,
        value: value?.toString() as string,
        sensitive: secretInputIds.has(key),
      }));

      createDeployment({
        variables: {
          id: version.id,
          input: {
            name: formData.name,
            space: formData.space,
            description: formData.description,
            deploymentType,
            inputs: createInputsPayload,
          },
        },
      })
        .then(({ data }) => {
          if (data?.templateDeploymentCreate) {
            if (submitCallback) {
              submitCallback(data.templateDeploymentCreate?.id);
            }
            reportSuccess({ message: `Deployment was successfully created` });
          }
        })
        .catch(onError);
    }
  };

  const resetSecretFieldActions = useCallback(() => {
    setSecretFieldActions(new Map());
  }, []);

  useEffect(() => {
    // update default values when inputs change but only if the form is not dirty
    if (!isDirty) {
      reset(defaultValues, { keepErrors: true });
      resetSecretFieldActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, isDirty]);

  return {
    builderForm,
    onSubmit,
    deploymentCreateLoading,
    loading,
    isUpdateMode,
    hasErrors,
    hasPreview,
    errors,
    setErrors,
    inputs,
    setInputs,
    secretFieldsState,
    isDirty,
    resetSecretFieldActions,
  };
};

export default useDeploymentForm;
